import { useEffect, useRef, useState, useCallback } from "react";
import { API } from "@/lib/api";

/**
 * Real-time ticker feed via WebSocket, with auto-reconnect + 30s fallback poll.
 * Returns { tickers, isLive } — tickers is a merged snapshot keyed by symbol.
 */
export function useLiveTickers() {
  const [tickers, setTickers] = useState({});
  const [isLive, setIsLive] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const retryTimerRef = useRef(null);
  const closedRef = useRef(false); // set on unmount — halts reconnect loop

  const connect = useCallback(() => {
    if (closedRef.current) return;
    try {
      const wsUrl = API.replace(/^http/, "ws").replace(/\/api$/, "") + "/api/ws/tickers";
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => {
        if (closedRef.current) { try { ws.close(); } catch { /* ignore */ } return; }
        setIsLive(true); retryRef.current = 0;
      };
      ws.onmessage = (ev) => {
        if (closedRef.current) return;
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "snapshot" && msg.tickers) {
            setTickers(msg.tickers);
          } else if (msg.type === "tick" && Array.isArray(msg.tickers)) {
            setTickers((prev) => {
              const next = { ...prev };
              for (const t of msg.tickers) next[t.symbol] = t;
              return next;
            });
          }
        } catch { /* ignore */ }
      };
      ws.onerror = () => { if (!closedRef.current) setIsLive(false); };
      ws.onclose = () => {
        if (closedRef.current) return;      // do NOT reconnect after unmount
        setIsLive(false);
        const delay = Math.min(30000, 1000 * 2 ** retryRef.current);
        retryRef.current += 1;
        retryTimerRef.current = setTimeout(connect, delay);
      };
    } catch {
      if (closedRef.current) return;
      setIsLive(false);
      retryTimerRef.current = setTimeout(connect, 5000);
    }
  }, []);

  useEffect(() => {
    closedRef.current = false;
    connect();
    return () => {
      closedRef.current = true;
      if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
      try { wsRef.current?.close(); } catch { /* ignore */ }
      wsRef.current = null;
    };
  }, [connect]);

  return { tickers, isLive };
}
